import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Calculator, IndianRupee, PieChart, CheckCircle, XCircle,
  AlertTriangle, ArrowRight, Share2, Copy, Send
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Progress } from "../ui/progress";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../ui/dialog";
import { toast } from "sonner";

const formatINR = (v) => {
  if (!v) return "0";
  const n = Number(v);
  if (n >= 10000000) return `${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)} L`;
  return n.toLocaleString("en-IN");
};

/**
 * Reusable EMI Calculator Component
 *
 * @param {string} apiBase - The API base URL (e.g., from config/api.js)
 * @param {string} leadId - If provided, calculations are logged to this lead
 * @param {object} prefill - Pre-fill form values
 * @param {boolean} compact - Compact mode for embedded views
 * @param {function} onCalculation - Callback when calculation is done
 * @param {boolean} showShareButton - Show share link button
 * @param {boolean} isPublicView - If true, this is a shared link view
 * @param {string} linkToken - Token for tracking public link usage
 */
const EMICalculator = ({
  apiBase,
  leadId,
  prefill,
  compact = false,
  onCalculation,
  showShareButton = true,
  isPublicView = false,
  linkToken
}) => {
  const [form, setForm] = useState({
    property_value: prefill?.property_value || 7500000,
    down_payment_pct: prefill?.down_payment_pct || 20,
    loan_tenure_years: prefill?.loan_tenure_years || 20,
    interest_rate: prefill?.interest_rate || 8.5,
    monthly_income: prefill?.monthly_income || 150000,
    stamp_duty_pct: prefill?.stamp_duty_pct || 6,
    registration_pct: prefill?.registration_pct || 1,
    under_construction: prefill?.under_construction || false,
  });
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);

  // Track that the tool was opened (for shared links)
  useEffect(() => {
    if (isPublicView && linkToken && apiBase) {
      axios.post(`${apiBase}/tools/track-open`, { token: linkToken }).catch(() => {});
    }
  }, [isPublicView, linkToken, apiBase]);

  const calculate = async () => {
    setCalculating(true);
    try {
      // Convert empty strings to 0 for calculation
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" || v === null || isNaN(v) ? 0 : v])
      );
      const endpoint = isPublicView
        ? `${apiBase}/tools/calculate-emi-public`
        : `${apiBase}/decision-support/emi-calculator`;
      const res = await axios.post(endpoint, payload);
      setResult(res.data);

      // Log calculation if lead context exists
      if ((leadId || linkToken) && apiBase) {
        try {
          await axios.post(`${apiBase}/tools/log-calculation`, {
            lead_id: leadId,
            link_token: linkToken,
            tool_type: "emi_calculator",
            inputs: form,
            outputs: {
              monthly_emi: res.data.monthly_emi,
              total_cost: res.data.total_cost,
              loan_amount: res.data.loan_amount,
              affordable: res.data.affordability?.affordable
            }
          });
        } catch (logErr) {
          console.error("Failed to log calculation:", logErr);
        }
      }

      if (onCalculation) onCalculation(form, res.data);
    } catch (err) {
      toast.error("Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  const createShareLink = async () => {
    if (!leadId) {
      toast.error("Cannot create share link without a lead");
      return;
    }
    setCreatingLink(true);
    try {
      const res = await axios.post(`${apiBase}/tools/create-link`, {
        lead_id: leadId,
        tool_type: "emi_calculator",
        prefill_data: form,
        expires_in_days: 30
      });
      setShareLink(res.data);
      setShareDialogOpen(true);
    } catch (err) {
      toast.error("Failed to create share link");
    } finally {
      setCreatingLink(false);
    }
  };

  const copyLink = () => {
    if (shareLink?.full_url) {
      navigator.clipboard.writeText(shareLink.full_url);
      toast.success("Link copied to clipboard");
    }
  };

  const sendViaWhatsApp = async () => {
    if (!shareLink?.full_url || !leadId) return;
    try {
      await axios.post(`${apiBase}/tools/send-link-whatsapp`, {
        lead_id: leadId,
        link_id: shareLink.link_id,
        tool_type: "emi_calculator"
      });
      toast.success("Link sent via WhatsApp");
      setShareDialogOpen(false);
    } catch (err) {
      toast.error("Failed to send via WhatsApp. You can copy the link instead.");
    }
  };

  const af = result?.affordability;

  return (
    <div className={`space-y-4 ${compact ? '' : 'p-4'}`} data-testid="emi-calculator-component">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-600" />
          <h3 className={`font-semibold text-slate-900 ${compact ? 'text-base' : 'text-lg'}`}>
            EMI Calculator
          </h3>
        </div>
        {showShareButton && leadId && !isPublicView && (
          <Button
            variant="outline"
            size="sm"
            onClick={createShareLink}
            disabled={creatingLink}
            data-testid="share-emi-btn"
          >
            <Share2 className="w-4 h-4 mr-1" />
            {creatingLink ? "Creating..." : "Share with Lead"}
          </Button>
        )}
      </div>

      <div className={`grid gap-4 ${compact ? 'lg:grid-cols-2' : 'lg:grid-cols-5'}`}>
        {/* Input Form */}
        <Card className={compact ? '' : 'lg:col-span-2'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Loan Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Property Value</label>
              <div className="relative">
                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  type="number"
                  className="pl-8 h-9 text-sm"
                  value={form.property_value}
                  onChange={(e) => setForm({ ...form, property_value: e.target.value === "" ? "" : parseInt(e.target.value) })}
                  data-testid="emi-property-value"
                />
              </div>
              <p className="text-xs text-slate-400 mt-0.5">{formatINR(form.property_value)}</p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Down Payment %</label>
                <Input
                  type="number"
                  className="h-9 text-sm"
                  value={form.down_payment_pct}
                  onChange={(e) => setForm({ ...form, down_payment_pct: parseInt(e.target.value) || 0 })}
                  data-testid="emi-down-payment"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Tenure (years)</label>
                <Input
                  type="number"
                  className="h-9 text-sm"
                  value={form.loan_tenure_years}
                  onChange={(e) => setForm({ ...form, loan_tenure_years: e.target.value === "" ? "" : parseInt(e.target.value) })}
                  data-testid="emi-tenure"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Interest Rate (%)</label>
              <Input
                type="number"
                step="0.1"
                className="h-9 text-sm"
                value={form.interest_rate}
                onChange={(e) => setForm({ ...form, interest_rate: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                data-testid="emi-interest-rate"
              />
            </div>

            <Separator className="my-2" />

            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Monthly Income (for affordability)</label>
              <div className="relative">
                <IndianRupee className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  type="number"
                  className="pl-8 h-9 text-sm"
                  value={form.monthly_income}
                  onChange={(e) => setForm({ ...form, monthly_income: e.target.value === "" ? "" : parseInt(e.target.value) })}
                  data-testid="emi-monthly-income"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Stamp Duty %</label>
                <Input
                  type="number"
                  step="0.5"
                  className="h-9 text-sm"
                  value={form.stamp_duty_pct}
                  onChange={(e) => setForm({ ...form, stamp_duty_pct: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Registration %</label>
                <Input
                  type="number"
                  step="0.5"
                  className="h-9 text-sm"
                  value={form.registration_pct}
                  onChange={(e) => setForm({ ...form, registration_pct: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-1">
              <label className="text-xs font-medium text-slate-600">Under Construction (GST applies)</label>
              <Switch
                checked={form.under_construction}
                onCheckedChange={(v) => setForm({ ...form, under_construction: v })}
              />
            </div>

            <Button
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={calculate}
              disabled={calculating}
              data-testid="calculate-emi-btn"
            >
              {calculating ? "Calculating..." : "Calculate EMI"}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {result ? (
          <div className={`space-y-4 ${compact ? '' : 'lg:col-span-3'}`}>
            {/* EMI Highlight */}
            <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
              <CardContent className="p-6 text-center">
                <p className="text-sm text-slate-300 mb-1">Monthly EMI</p>
                <p className="text-3xl font-bold" data-testid="emi-result">
                  {formatINR(result.monthly_emi)}
                </p>
                <p className="text-xs text-slate-400 mt-2">
                  for {result.tenure_years} years at {result.interest_rate}% p.a.
                </p>
              </CardContent>
            </Card>

            {/* Breakdown Cards */}
            <div className="grid grid-cols-3 gap-3">
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-slate-500">Loan Amount</p>
                  <p className="text-lg font-bold text-slate-900">{formatINR(result.loan_amount)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-slate-500">Total Interest</p>
                  <p className="text-lg font-bold text-red-600">{formatINR(result.total_interest)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-slate-500">Total Payment</p>
                  <p className="text-lg font-bold text-slate-900">{formatINR(result.total_payment)}</p>
                </CardContent>
              </Card>
            </div>

            {/* Principal vs Interest Bar */}
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium text-slate-700 mb-3">Principal vs Interest Split</p>
                <div className="flex h-6 rounded-full overflow-hidden">
                  <div className="bg-blue-500 flex items-center justify-center" style={{ width: `${result.principal_pct}%` }}>
                    <span className="text-[10px] font-bold text-white">{result.principal_pct}%</span>
                  </div>
                  <div className="bg-red-400 flex items-center justify-center" style={{ width: `${result.interest_pct}%` }}>
                    <span className="text-[10px] font-bold text-white">{result.interest_pct}%</span>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>Principal: {formatINR(result.loan_amount)}</span>
                  <span>Interest: {formatINR(result.total_interest)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Additional Costs */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <PieChart className="w-4 h-4" /> Additional Costs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: "Stamp Duty", value: result.additional_costs?.stamp_duty, color: "bg-amber-500" },
                  { label: "Registration", value: result.additional_costs?.registration, color: "bg-emerald-500" },
                  ...(result.additional_costs?.gst > 0 ? [{ label: "GST", value: result.additional_costs.gst, color: "bg-purple-500" }] : []),
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${item.color}`} />
                      <span className="text-slate-600">{item.label}</span>
                    </div>
                    <span className="font-medium">{formatINR(item.value)}</span>
                  </div>
                ))}
                <Separator />
                <div className="flex items-center justify-between text-sm font-semibold">
                  <span>Total Cost</span>
                  <span className="text-emerald-700">{formatINR(result.total_cost)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Affordability */}
            {af && af.status && (
              <Card className={`border-2 ${af.status === "comfortable" ? "border-emerald-200 bg-emerald-50/30" : af.status === "stretched" ? "border-amber-200 bg-amber-50/30" : "border-red-200 bg-red-50/30"}`}
                data-testid="affordability-card"
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    {af.status === "comfortable" ? <CheckCircle className="w-5 h-5 text-emerald-500" /> :
                     af.status === "stretched" ? <AlertTriangle className="w-5 h-5 text-amber-500" /> :
                     <XCircle className="w-5 h-5 text-red-500" />}
                    Affordability: <span className="capitalize">{af.status?.replace(/_/g, " ")}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-slate-600">EMI-to-Income Ratio</span>
                      <span className={`text-sm font-bold ${af.emi_to_income_ratio <= 30 ? "text-emerald-600" : af.emi_to_income_ratio <= 40 ? "text-amber-600" : "text-red-600"}`}>
                        {af.emi_to_income_ratio}%
                      </span>
                    </div>
                    <Progress value={Math.min(af.emi_to_income_ratio || 0, 100)} className="h-2" />
                    <p className="text-xs text-slate-400 mt-1">Recommended: below 40%</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Max Affordable EMI</p>
                      <p className="font-bold text-slate-900">{formatINR(af.max_affordable_emi)}/mo</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Max Eligible Loan</p>
                      <p className="font-bold text-slate-900">{formatINR(af.max_eligible_loan)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-slate-500">Max Property Value (at {form.down_payment_pct}% down)</p>
                      <p className="font-bold text-blue-600">{formatINR(af.max_property_value)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Yearly Breakup */}
            {result.yearly_breakup?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Year-wise Breakup</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-64 overflow-y-auto">
                    <div className="space-y-1">
                      {result.yearly_breakup.map((yr) => (
                        <div key={yr.year} className="flex items-center gap-2 text-xs py-1 border-b last:border-0">
                          <span className="w-10 text-slate-500 font-medium">Y{yr.year}</span>
                          <div className="flex-1 flex h-3 rounded-full overflow-hidden bg-slate-100">
                            <div className="bg-blue-500" style={{ width: `${(yr.principal / (yr.principal + yr.interest)) * 100}%` }} />
                            <div className="bg-red-400" style={{ width: `${(yr.interest / (yr.principal + yr.interest)) * 100}%` }} />
                          </div>
                          <span className="w-16 text-right text-slate-600">{formatINR(yr.remaining)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className={compact ? '' : 'lg:col-span-3'}>
            <Card>
              <CardContent className="p-12 text-center text-slate-400">
                <Calculator className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>Enter details and click Calculate to see results</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Share Link Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share EMI Calculator with Lead</DialogTitle>
          </DialogHeader>

          {shareLink && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Share this personalized link with the lead. When they use the calculator,
                their inputs will be tracked and logged to their profile.
              </p>

              <div className="flex items-center gap-2">
                <Input
                  value={shareLink.full_url}
                  readOnly
                  className="text-sm"
                  data-testid="share-link-input"
                />
                <Button variant="outline" size="icon" onClick={copyLink} data-testid="copy-link-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600">
                <p className="font-medium mb-1">Pre-filled values:</p>
                <p>Property: {formatINR(form.property_value)} | Tenure: {form.loan_tenure_years} yrs | Rate: {form.interest_rate}%</p>
              </div>

              <p className="text-xs text-slate-500">
                Link expires in 30 days. You can copy and send this link via any channel.
              </p>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={copyLink} data-testid="dialog-copy-link-btn">
              <Copy className="w-4 h-4 mr-2" /> Copy Link
            </Button>
            <Button onClick={sendViaWhatsApp} className="bg-green-600 hover:bg-green-700" data-testid="send-whatsapp-btn">
              <Send className="w-4 h-4 mr-2" /> Send via WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EMICalculator;
